import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Modal, Button, Row, Col, Spinner } from 'react-bootstrap';

const AddAttendance = ({ show, onHide, onAttendanceAdded }) => {
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [attStatuses, setAttStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const formRef = useRef(null);
  const suggestionsRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [formData, setFormData] = useState({
    emp_id: '',
    att_date: '',
    status_id: '',
    att_value: '',
    in_time: '',
    out_time: '',
    work_hours: '',
    remarks: '',
    in_lat: '',
    in_lng: '',
    out_lat: '',
    out_lng: '',
    in_face_score: '',
    out_face_score: '',
    in_verified: 0,
    out_verified: 0,
    punch_source: 'MANUAL',
  });

  const API_BASE = process.env.REACT_APP_API_BASE;

  useEffect(() => {
    if (show) {
      fetchAttStatuses();
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        formRef.current &&
        !formRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchAttStatuses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_BASE}/get_att_statuses.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAttStatuses(response.data.statuses || []);
      }
    } catch (error) {
      console.error('Error fetching attendance statuses:', error);
      setAttStatuses([]);
    }
  };

  const searchEmployees = async (searchTerm) => {
    try {
      const token = localStorage.getItem('authToken');
      const baseUrl = API_BASE.endsWith('/') ? API_BASE : `${API_BASE}/`;

      const response = await axios.get(`${baseUrl}get_employees.php`, {
        params: { search: searchTerm },
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.employees) {
        setFilteredEmployees(response.data.employees);
        setShowSuggestions(response.data.employees.length > 0);
      } else {
        setFilteredEmployees([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      console.error('Error searching employees:', error);
      setFilteredEmployees([]);
      setShowSuggestions(false);
    }
  };

  const handleEmployeeSearchChange = (e) => {
    const value = e.target.value;
    setEmployeeSearch(value);
    setFormData(prev => ({ ...prev, emp_id: '' }));
    setFilteredEmployees([]);
    setShowSuggestions(false);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        searchEmployees(value.trim());
      }, 300);
    }
  };

  const selectEmployee = (employee) => {
    setEmployeeSearch(`${employee.emp_code} - ${employee.first_name} ${employee.last_name}`);
    setFormData(prev => ({ ...prev, emp_id: employee.emp_id }));
    setFilteredEmployees([]);
    setShowSuggestions(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'status_id') {
      // Handle status selection
      if (value) {
        const selectedStatus = attStatuses.find(status => status.status_id.toString() === value.toString());
        if (selectedStatus) {
          setFormData(prev => ({
            ...prev,
            status_id: value,
            att_value: selectedStatus.att_value || '0.00'
          }));
        }
      } else {
        // If status is cleared
        setFormData(prev => ({
          ...prev,
          status_id: '',
          att_value: ''
        }));
      }
    } else if (name !== 'att_value') {
      // Allow changes to all fields except att_value
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Auto-calculate work hours if both in_time and out_time are provided
      if ((name === 'in_time' || name === 'out_time') && formData.in_time && formData.out_time) {
        calculateWorkHours(formData.in_time, formData.out_time);
      }
    }
    // att_value field is read-only and handled by status selection
  };

  const calculateWorkHours = (inTime, outTime) => {
    if (!inTime || !outTime) return;

    const [inHours, inMinutes] = inTime.split(':').map(Number);
    const [outHours, outMinutes] = outTime.split(':').map(Number);

    let totalMinutes = (outHours * 60 + outMinutes) - (inHours * 60 + inMinutes);

    if (totalMinutes < 0) {
      totalMinutes += 24 * 60;
    }

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const workHours = hours + (minutes / 60);

    setFormData(prev => ({
      ...prev,
      work_hours: workHours.toFixed(2)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.emp_id) {
      setError('Please select an employee');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('authToken');

      const attendanceData = {
        emp_id: formData.emp_id,
        att_date: formData.att_date,
        status_id: formData.status_id || null,
        att_value: formData.att_value || 0,
        in_time: formData.in_time || null,
        out_time: formData.out_time || null,
        work_hours: formData.work_hours || null,
        remarks: formData.remarks || null,
        in_lat: formData.in_lat || null,
        in_lng: formData.in_lng || null,
        out_lat: formData.out_lat || null,
        out_lng: formData.out_lng || null,
        in_face_score: formData.in_face_score || null,
        out_face_score: formData.out_face_score || null,
        in_verified: formData.in_verified,
        out_verified: formData.out_verified,
        punch_source: formData.punch_source || 'MANUAL',
      };

      const response = await axios.post(`${API_BASE}/add_attendance.php`,
        attendanceData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onAttendanceAdded();
        onHide();
        resetForm();
      } else {
        setError(response.data.error || 'Failed to add attendance record');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add attendance record');
      console.error('Error adding attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      emp_id: '',
      att_date: new Date().toISOString().split('T')[0],
      status_id: '',
      att_value: '',
      in_time: '',
      out_time: '',
      work_hours: '',
      remarks: '',
      in_lat: '',
      in_lng: '',
      out_lat: '',
      out_lng: '',
      in_face_score: '',
      out_face_score: '',
      in_verified: 0,
      out_verified: 0,
      punch_source: 'MANUAL',
    });
    setEmployeeSearch('');
    setFilteredEmployees([]);
    setShowSuggestions(false);
    setError('');

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleModalClose = () => {
    resetForm();
    onHide();
  };

  const handleCalculateWorkHours = () => {
    if (formData.in_time && formData.out_time) {
      calculateWorkHours(formData.in_time, formData.out_time);
    } else {
      setError('Please provide both in time and out time');
    }
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={handleModalClose}
      size="lg"
      centered
      scrollable
      backdrop="static"
      animation={false}
    >
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <i className="bi bi-plus-circle me-2"></i>
          Add Attendance Record
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        <form onSubmit={handleSubmit} ref={formRef}>
          {error && (
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>{error}</div>
              <button
                type="button"
                className="btn-close ms-auto"
                onClick={() => setError('')}
              ></button>
            </div>
          )}

          <Row className="g-3">
            <Col md={6}>
              <label className="form-label">Employee *</label>
              <div className="position-relative">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search employee by name or code..."
                  value={employeeSearch}
                  onChange={handleEmployeeSearchChange}
                  required
                  autoComplete="off"
                />
                {formData.emp_id && (
                  <input type="hidden" name="emp_id" value={formData.emp_id} />
                )}
                {showSuggestions && filteredEmployees.length > 0 && (
                  <div
                    ref={suggestionsRef}
                    className="list-group position-absolute w-100 shadow-lg mt-1"
                    style={{ zIndex: 1050, maxHeight: '250px', overflowY: 'auto' }}
                  >
                    {filteredEmployees.map(employee => (
                      <button
                        key={employee.emp_id}
                        type="button"
                        className="list-group-item list-group-item-action py-2 px-3"
                        onClick={() => selectEmployee(employee)}
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="fw-bold text-primary">{employee.emp_code}</span>
                          <span className="small text-muted">{employee.dept_name || 'N/A'}</span>
                        </div>
                        <div className="fw-semibold">
                          {employee.first_name} {employee.last_name}
                        </div>
                        <div className="small text-muted">
                          {employee.desig_name || 'N/A'}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-text">
                Start typing to search employees (min. 2 characters)
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Date *</label>
              <input
                type="date"
                className="form-control"
                name="att_date"
                value={formData.att_date}
                onChange={handleInputChange}
                max={getTodayDate()}
                required
              />
            </Col>

            <Col md={6}>
              <label className="form-label">Attendance Status</label>
              <select
                className="form-select"
                name="status_id"
                value={formData.status_id}
                onChange={handleInputChange}
              >
                <option value="">Select Status</option>
                {attStatuses.map(status => (
                  <option key={status.status_id} value={status.status_id}>
                    {status.status_name} ({status.att_value})
                  </option>
                ))}
              </select>
            </Col>

            <Col md={6}>
              <label className="form-label">Attendance Value</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                className="form-control bg-light"
                name="att_value"
                value={formData.att_value}
                onChange={handleInputChange}
                placeholder="Auto-set from status"
                readOnly
              />
              <div className="form-text">
                Auto-set from selected status (read-only)
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">In Time</label>
              <input
                type="time"
                className="form-control"
                name="in_time"
                value={formData.in_time}
                onChange={handleInputChange}
              />
            </Col>

            <Col md={6}>
              <label className="form-label">Out Time</label>
              <div className="input-group">
                <input
                  type="time"
                  className="form-control"
                  name="out_time"
                  value={formData.out_time}
                  onChange={handleInputChange}
                />
                <Button
                  variant="outline-secondary"
                  onClick={handleCalculateWorkHours}
                  title="Calculate Work Hours"
                >
                  <i className="bi bi-calculator"></i>
                </Button>
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Work Hours</label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="24"
                  className="form-control"
                  name="work_hours"
                  value={formData.work_hours}
                  onChange={handleInputChange}
                  placeholder="Auto-calculated"
                />
                <span className="input-group-text">hours</span>
              </div>
              <div className="form-text">
                Auto-calculated from in/out times
              </div>
            </Col>

            <Col md={6}>
              <label className="form-label">Status Preview</label>
              <div className="card bg-light">
                <div className="card-body py-2">
                  <Row className="small">
                    <Col xs={6}>
                      <strong>Value:</strong> {formData.att_value || '0.00'}
                    </Col>
                    <Col xs={6}>
                      <strong>Hours:</strong> {formData.work_hours || '0.00'}
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>

            <Col xs={12}>
              <label className="form-label">Remarks</label>
              <textarea
                className="form-control"
                name="remarks"
                value={formData.remarks}
                onChange={handleInputChange}
                rows="3"
                placeholder="Additional notes or comments..."
              />
            </Col>

            {/* Punch Source */}
            <Col md={4}>
              <label className="form-label">Punch Source</label>
              <select
                className="form-select"
                name="punch_source"
                value={formData.punch_source}
                onChange={handleInputChange}
              >
                <option value="MANUAL">MANUAL</option>
                <option value="MOBILE">MOBILE</option>
                <option value="DEVICE">DEVICE</option>
              </select>
            </Col>

            {/* Verification Flags */}
            <Col md={4}>
              <label className="form-label">In Verified</label>
              <select
                className="form-select"
                name="in_verified"
                value={formData.in_verified}
                onChange={handleInputChange}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </Col>

            <Col md={4}>
              <label className="form-label">Out Verified</label>
              <select
                className="form-select"
                name="out_verified"
                value={formData.out_verified}
                onChange={handleInputChange}
              >
                <option value={0}>No</option>
                <option value={1}>Yes</option>
              </select>
            </Col>

            {/* Location Fields - In */}
            <Col xs={12}>
              <div className="card border-0 bg-light">
                <div className="card-body py-2">
                  <p className="fw-semibold mb-2 small text-muted">
                    <i className="bi bi-geo-alt me-1"></i>Location (optional — typically captured by mobile/device)
                  </p>
                  <Row className="g-2">
                    <Col md={3}>
                      <label className="form-label small mb-1">In Latitude</label>
                      <input
                        type="number"
                        step="0.000001"
                        className="form-control form-control-sm"
                        name="in_lat"
                        value={formData.in_lat}
                        onChange={handleInputChange}
                        placeholder="e.g. 28.613939"
                      />
                    </Col>
                    <Col md={3}>
                      <label className="form-label small mb-1">In Longitude</label>
                      <input
                        type="number"
                        step="0.000001"
                        className="form-control form-control-sm"
                        name="in_lng"
                        value={formData.in_lng}
                        onChange={handleInputChange}
                        placeholder="e.g. 77.209023"
                      />
                    </Col>
                    <Col md={3}>
                      <label className="form-label small mb-1">Out Latitude</label>
                      <input
                        type="number"
                        step="0.000001"
                        className="form-control form-control-sm"
                        name="out_lat"
                        value={formData.out_lat}
                        onChange={handleInputChange}
                        placeholder="e.g. 28.613939"
                      />
                    </Col>
                    <Col md={3}>
                      <label className="form-label small mb-1">Out Longitude</label>
                      <input
                        type="number"
                        step="0.000001"
                        className="form-control form-control-sm"
                        name="out_lng"
                        value={formData.out_lng}
                        onChange={handleInputChange}
                        placeholder="e.g. 77.209023"
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>

            {/* Face Score Fields */}
            <Col md={6}>
              <label className="form-label">In Face Score</label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="form-control"
                  name="in_face_score"
                  value={formData.in_face_score}
                  onChange={handleInputChange}
                  placeholder="0.00 – 100.00"
                />
                <span className="input-group-text">%</span>
              </div>
              <div className="form-text">Face recognition confidence score at punch-in</div>
            </Col>

            <Col md={6}>
              <label className="form-label">Out Face Score</label>
              <div className="input-group">
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  className="form-control"
                  name="out_face_score"
                  value={formData.out_face_score}
                  onChange={handleInputChange}
                  placeholder="0.00 – 100.00"
                />
                <span className="input-group-text">%</span>
              </div>
              <div className="form-text">Face recognition confidence score at punch-out</div>
            </Col>
          </Row>
        </form>
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <Button
          variant="secondary"
          onClick={handleModalClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Adding...
            </>
          ) : (
            <>
              <i className="bi bi-plus-circle me-2"></i>
              Add Record
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddAttendance;